/**
 * The FilterGroup component is an organism which utilizes the Accordion and FilterListItem components to display a grouping of product filtering options
 *
 * @module views/Layouts/FilterGroup
 * @memberof -Common
 */
import './FilterGroup.scss';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';

import Accordion from '@ulta/core/components/Accordion/Accordion';
import Button from '@ulta/core/components/Button/Button';
import InputFieldUnderline from '@ulta/core/components/InputFieldUnderline/InputFieldUnderline';
import Text from '@ulta/core/components/Text/Text';
import { useTimeout } from '@ulta/core/hooks/useTimeout/useTimeout';
import { useUserContext } from '@ulta/core/providers/UserContextProvider/UserContextProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import { useFiltersColumnContext } from '@ulta/providers/FiltersColumnProvider/FiltersColumnProvider';

import { FILTER_EVENT, FILTER_GROUPS, FILTER_TYPES } from '@ulta/utils/constants/filters';
import interpolateString from '@ulta/utils/interpolateString/interpolateString';

import FilterListItem from '../FilterListItem/FilterListItem';
import NestedAccordion from '../NestedAccordion/NestedAccordion';
import * as utils from './FilterGroup';

/**
 * Represents a FilterGroup component
 *
 * @method
 * @param {FilterGroupProps} props - React properties passed from composition
 * @returns FilterGroup
 */
export const FilterGroup = function( props ){
  const groupRef = useRef();
  const { id, title, seeMoreLabel, viewLessLabel, filters, seeMoreAriaLabel, viewLessAriaLabel, componentKey, searchBar, noResultsLabel, resetAction, setFilterRequest, appliedFilters } = props;
  const { loading, seeMore, setSeeMore, toggleFilterGroupListItem } = useFiltersColumnContext();
  const type = props.type?.toUpperCase();
  const hasRangeFilter = type === FILTER_TYPES.MULTI ? groupHasRangeFilter( { filters } ) : '';
  const [captureLastEvent, setCaptureLastEvent] = useState( null );
  const [seeMoreToogle, setSeeMoreToggle] = useState( false );
  const [searchValue, setSearchValue] = useState( '' );
  const [expandMore, setExpandMore] = useState( false );

  const [timeout] = useTimeout();
  const { store } = useUserContext();

  const updatedFilters = useMemo( () => utils.composeUpdatedFilters( { filters, searchValue } ), [filters, searchValue] );
  const seeMoreItems = seeMoreItemsLength( {
    seeMoreListLength: SEE_MORE_LIST_LENGTH,
    updatedFilters,
    type,
    seeMoreLabel
  } );

  const seeMoreExpanded = seeMore[title] === false;

  const handleSeeMoreFocus = useCallback( utils.composeHandleSeeMoreFocus( { groupRef, seeMoreExpanded, captureLastEvent }, { timeout } ), [groupRef, seeMoreExpanded, captureLastEvent] );

  const getStoreFilterProps = useCallback(
    utils.composeGetStoreFilterProps( { store, appliedFilters }, { interpolateString }
    ), [interpolateString, store, appliedFilters]
  );

  // Compose input change handler
  // -> handles search value changes
  const inputChangeHandler = useCallback(
    utils.composeInputChangeHandler( { setSearchValue } ),
    []
  );

  // Compose reset handler
  // -> handles 'x' button when user clears a search
  const resetSearchAction = useCallback(
    utils.composeHandleResetAction( { setSearchValue } ),
    []
  );

  const resetRatingAction = useCallback(
    utils.composeResetRatingAction( { action: resetAction, resetEvent: FILTER_EVENT.RESET_GROUP }, { setFilterRequest } ),
    [resetAction, setFilterRequest]
  );

  useEffect( handleSeeMoreFocus, [handleSeeMoreFocus] );

  if( !type || !hasItems( filters ) ){
    return null;
  }

  const showSeeMoreButton = updatedFilters?.length > SEE_MORE_LIST_LENGTH && !hasRangeFilter;

  // Derived state for list display vs. single item accordion (used for categories/nested tree)
  const isFilterList = type === FILTER_TYPES.MULTI || type === FILTER_TYPES.RANGE;
  const isCategory = type === FILTER_TYPES.SINGLE && id === FILTER_GROUPS.CATEGORIES;
  const isRatings = type === FILTER_TYPES.SINGLE && id === FILTER_GROUPS.RATING;
  const isStore = type === FILTER_TYPES.SINGLE && id === FILTER_GROUPS.AVAILABLE_STORE_IDS;
  const isDates = type === FILTER_TYPES.SINGLE && id === FILTER_GROUPS.DATE;
  const isMultiStoreSelect = type === FILTER_TYPES.MULTI && id === FILTER_GROUPS.AVAILABLE_STORE_IDS; // multi store feature
  const resultCountLabel = `${updatedFilters?.length} ${noResultsLabel}`;
  const hasStoreList = isStore || isMultiStoreSelect ;

  return (
    <div className='FilterGroup'
      ref={ groupRef }
    >
      <Accordion
        title={ title }
        titleTag='h2'
        expandMore={ expandMore }
        setExpandMore={ setExpandMore }
      >
        { isFilterList && (
          <>
            { !!searchBar && (
              <div className='FilterGroup__searchContainer'>
                <Formik
                  initialValues={ { search: searchValue } }
                >
                  { () => (
                    <Form
                      role='search'
                      className='FilterGroup__form'
                    >
                      <InputFieldUnderline
                        type='search'
                        name='search'
                        autoComplete='off'
                        leftIcon='Search'
                        onChange={ inputChangeHandler }
                        handleResetVal={ resetSearchAction }
                        placeholder={ searchBar.placeholderLabel }
                        displayGoButton={ false }
                        clearValueText={ searchBar.clearAccessibilityLabel }
                      />
                    </Form>
                  ) }
                </Formik>

                <div aria-live='assertive'>
                  <span className='sr-only'>{ resultCountLabel }</span>
                </div>
                { updatedFilters?.length === 0 && (
                  <Text
                    htmlTag='span'
                    textStyle='body-3'
                    color='neutral-600'
                    ariaHidden={ true }
                  >
                    { resultCountLabel }
                  </Text>
                ) }
              </div>
            ) }
            { !isMultiStoreSelect &&
              <ul>
                { updatedFilters.map( ( currentFilter, index ) => (
                  <FilterListItem key={ `${currentFilter.title}_${index}` }
                    { ...spreadProps( { ...currentFilter } ) }
                    hideItems={ index >= SEE_MORE_LIST_LENGTH && !seeMoreToogle }
                    searchValue={ searchValue }
                  />
                ) ) }

                { showSeeMoreButton && (
                  <li className={ seeMoreExpanded ? 'FilterGroup__ViewLess' : 'FilterGroup__SeeMore' }>
                    <Button
                      likeLink
                      label={ seeMoreExpanded ? viewLessLabel : seeMoreItems }
                      ariaLabel={ seeMoreExpanded ? viewLessAriaLabel : seeMoreAriaLabel }
                      disabled={ loading }
                      onClick={ ( event ) => {
                        utils.handleOnClick( { seeMoreToogle, event, expandMore }, { setSeeMoreToggle, setCaptureLastEvent, setExpandMore } );
                        setSeeMore( {
                          ...( seeMore && Object.keys( seeMore ).length ),
                          [`${title}`]: seeMoreExpanded
                        } );
                      }
                      }
                    />
                  </li>
                ) }
              </ul>
            }
          </>
        ) }

        { ( isRatings || isDates ) && (
          <fieldset>
            <legend className='sr-only'>{ title }</legend>
            <ul>
              { updatedFilters.map( ( currentFilter, index ) => (
                <FilterListItem key={ `${currentFilter.title}_${index}` }
                  { ...spreadProps( { ...currentFilter } ) }
                />
              ) ) }
              <li className='FilterGroup__Clear'>
                <Button
                  likeLink
                  label={ resetAction.label }
                  ariaLabel={ resetAction.label }
                  disabled={ !resetAction.graphql }
                  onClick={ resetRatingAction }
                />
              </li>
            </ul>
          </fieldset>
        ) }

        { hasStoreList && (
          <fieldset>
            <legend className='sr-only'>{ title }</legend>
            <ul>
              { updatedFilters.map( ( currentFilter, index ) => {
                const { title, id, store, buttonLabel, buttonAriaLabel, buttonStoreAction } =
                getStoreFilterProps( currentFilter ); // need to check why added this
                return (
                  <div key={ `${title}_${index}` } >
                    <FilterListItem
                      { ...spreadProps( { ...currentFilter } ) }
                      id={ id }
                      title={ title }
                      store={ store }
                    />
                    { index === ( updatedFilters.length - 1 ) &&
                      <li className='FilterGroup__SelectStore' >
                        <Button
                          likeLink
                          label={ buttonLabel }
                          ariaLabel={ buttonAriaLabel }
                          action={ buttonStoreAction }
                        />
                      </li>
                    }
                  </div>
                );
              } ) }
            </ul>
          </fieldset>
        ) }

        { isCategory && (
          <NestedAccordion
            categoryData={ updatedFilters }
            seeMoreLabel={ seeMoreItems }
            viewLessLabel={ viewLessLabel }
            seeMoreListLength={ SEE_MORE_LIST_LENGTH }
            toggleFilterGroupListItem={ toggleFilterGroupListItem }
            seeMore={ seeMore }
            setSeeMore={ setSeeMore }
            componentKey={ componentKey }
          />
        ) }
      </Accordion>
    </div>
  );
};

/**
 * How many items to show in a filter bucket before a displaying see more button
 * @constant {number}
 * @default
 */
export const SEE_MORE_LIST_LENGTH = 5;

/**
 *
 * @method composeResetRatingAction
 * @param {object} data - Data passed as an argument
 * @param {object} data.action - select Action to hit graphQL
 * @param {object} methods - Methods passed as an argument
 */
export const composeResetRatingAction = ( data, methods ) => ( ) => {
  const { action, resetEvent } = data || {};
  const { setFilterRequest } = methods || {};

  if( !setFilterRequest || !action || !resetEvent ){
    return;
  }

  setFilterRequest( {
    action,
    id: `reset-group-${Date.now()}`,
    event: resetEvent
  } );
};

/**
 *
 * @method handleOnClick
 * @param {object} data - Data passed as an argument
 * @param {object} methods - Methods passed as an argument
 */
export const handleOnClick = ( data, methods ) => {
  const { seeMoreToogle, event, expandMore } = data || {};
  const { setSeeMoreToggle, setCaptureLastEvent, setExpandMore } = methods || {};

  if( !setSeeMoreToggle || !setCaptureLastEvent || !setExpandMore ){
    return;
  }

  setSeeMoreToggle( !seeMoreToogle );
  setCaptureLastEvent( event.detail );
  setExpandMore( !expandMore );
};
/**
 * @method updatedFilters
 * @summary Method to filter the searched filters
 * @param {object} data - argument of filters and seach text
 */
export const composeUpdatedFilters = ( data ) => {

  const { filters = [], searchValue = '' } = handleEmptyObjects( data );

  if( !searchValue ){
    return filters;
  }

  return searchValue &&
    filters.filter( ( val ) => val?.title?.toLowerCase()?.includes( searchValue?.toLowerCase() ) );
};

/**
 * @method checkFilterType
 * @summary Method to check the type is range in updatedFilters
 * @param {object} data - argument of updatedFilters items
 */
export const groupHasRangeFilter = ( { updatedFilters } ) =>
  updatedFilters?.some( ( f ) => f.type?.toUpperCase() === FILTER_TYPES.RANGE ) || false;

/**
 * @method handleFocus
 * @summary Handle the focus to list item when 'more' link is clicked
 * @param {string} id - filter Id
 * @returns {void}
 */
export const composeHandleSeeMoreFocus = ( data = {}, methods = {} ) => ()=>{
  const { groupRef, seeMoreExpanded, captureLastEvent } = data;
  const { timeout } = methods;
  if( !groupRef?.current || !seeMoreExpanded || captureLastEvent === 1 || !timeout ){
    return;
  }
  timeout( ()=>{
    const input = groupRef.current?.querySelectorAll( 'ul input' );
    if( input ){
      const listItems = Array.from( input ) ;
      const focusedItem = listItems?.[SEE_MORE_LIST_LENGTH];
      focusedItem && focusedItem.focus() ;
    }
  }, 100 );
};

/**
 * @function composeGetStoreFilterProps
 * @param {Object} data - The data object containing filter by store information.
 * @param {Object} data.store - Store stored in local storage from UserContext.
 * @param {array} data.appliedFilters - array containing applied filters.
 * @param {Object} methods - The methods object containing utility functions.
 * @param {Object} methods.interpolateString - Utility for string interpolation.
 * @param {Object} filter - The filter object containing store action and label information.
 * @param {string} filter.storeNotSelectedLabel - The label displayed when no store is selected.
 * @param {string} filter.storeSelectedLabel - The label displayed when a store is selected.
 * @param {Object} filter.selectStoreAction - The action object for selecting a store.
 * @param {string} filter.selectStoreAction.label - The label for selecting a store.
 * @param {Object} filter.changeStoreAction - The action object for changing the store.
 * @param {string} filter.changeStoreAction.label - The label for changing the store.
 * @returns {Object} An object containing properties for the filter button and title.
 * @returns {string} return.title - The title for the filter button.
 * @returns {string} return.id - The unique ID for the filter button.
 * @returns {string} return.buttonLabel - The label for the filter button.
 * @returns {string} return.buttonAriaLabel - The aria-label for the filter button.
 * @returns {Object} return.buttonStoreAction - The action object for the filter button.
 * @returns {Object} return.store - The store object for displaying store names.
 * */
export const composeGetStoreFilterProps = ( data, methods ) => ( filter ) => {
  const { store, appliedFilters } = data || {};
  const { interpolateString } = methods || {};

  if( !filter || !interpolateString ){
    return;
  }

  let filterTitle = filter?.storeNotSelectedLabel;
  let filterId = `remove-store`;

  const storeFilter = appliedFilters?.find(
    ( filter ) => filter?.groupType === FILTER_GROUPS.AVAILABLE_STORE_IDS
  );

  const { bopisStoreId: storeId, bopisStoreName: storeName } = store || {};

  const storeFromFilters = getStoreFromFilters( { storeFilter, storeId, storeName } );

  let buttonLabel = filter?.selectStoreAction?.label;
  let buttonAriaLabel = filter?.selectStoreAction?.label;
  let buttonStoreAction = filter?.selectStoreAction;

  if( storeId || filter.selected ){
    const name = filter.title || storeName || '';
    const id = filter.id || storeId;
    filterTitle = filter?.storeSelectedLabel ? interpolateString( filter?.storeSelectedLabel, [name] ) : name;
    filterId = `select-store-${id}`;

    buttonLabel = filter?.changeStoreAction?.label;
    buttonAriaLabel = filter?.changeStoreAction?.label;
    buttonStoreAction = filter?.changeStoreAction;
  }

  return {
    title: filterTitle,
    id: filterId,
    buttonLabel: buttonLabel,
    buttonAriaLabel: buttonAriaLabel,
    buttonStoreAction: buttonStoreAction,
    store: storeFromFilters || store
  };
};

/**
 * Extracts store filter information based on the provided data.
 *
 * @param {Object} data - The input data object.
 * @param {Object} [data.storeFilter] - The store filter object.
 * @param {string} [data.storeFilter.id] - The ID of the store filter.
 * @param {string} [data.storeFilter.title] - The title of the store filter.
 * @param {string} [data.storeId] - The ID of the currently selected store.
 * @param {string} [data.storeName] - The name of the currently selected store.
 * @returns {{ bopisStoreId: string, bopisStoreName: string } | null} The extracted store filter details or null if no valid store filter exists.
 */
export const getStoreFromFilters = ( data ) => {
  const {
    storeFilter, storeId, storeName
  } = data || {};


  if( !storeFilter?.id ){
    return null;
  }

  const storeFromFilters = {
    bopisStoreId: storeFilter.id,
    bopisStoreName: storeFilter.title || ''
  };

  if( !storeFromFilters.bopisStoreName && storeFilter.id === storeId ){
    storeFromFilters.bopisStoreName = storeName;
  }

  return storeFromFilters;
};

/**
 * @method seeMoreItemsLength
 * @summary Method to check and replace (%1) in seemore label with reamining items length in updatedFilters
 * @param {object} data - argument of data items
 * @returns {string} Returns an string with reamining length value
 */
export const seeMoreItemsLength = ( data ) => {
  const { seeMoreListLength, updatedFilters = [], type, seeMoreLabel } = data;
  let filterLength =  updatedFilters.length ;
  if( filterLength === 0 ){
    return null;
  }
  let filterRemainingLength = Number( filterLength - seeMoreListLength );
  if( type?.toUpperCase() === FILTER_TYPES.SINGLE ){
    if( updatedFilters.some( item => item.level === 2 && item.selected ) ){
      filterRemainingLength = filterRemainingLength - 3 ;
    }
    else {
      filterRemainingLength = filterRemainingLength - 2 ;
    }
  }
  return seeMoreLabel?.replace( '%1', ' ' + filterRemainingLength + ' ' );
};

/**
 * Method to handle search value
 * @param {object} methods methods
 * @param {action} methods.setSearchValue set search value
 */
export const composeInputChangeHandler = ( methods ) => ( searchString ) => {
  const { setSearchValue } = methods || {};

  if( !setSearchValue ){
    return;
  }

  setSearchValue( searchString );
};

/**
 * Method to handle reset action
 * @param {action} methods.setSearchValue set search value empty string on reset
 */
export const composeHandleResetAction = ( methods ) => () => {
  const { setSearchValue } = methods || {};

  if( !setSearchValue ){
    return;
  }

  setSearchValue( '' );
};

/**
 * Property type definitions
 * @typedef FilterGroupProps
 * @type {object}
 * @property {object} propTypes
 * @property {array} filters - array containing filter list items
 * @property {string} title - displays the name of current filter group
 * @property {object} seeMoreAction - A action which have see more link and label
 * @property {string} type - filter group type. multi|single
 * @property {string} id - filter id used by adeptmind
 * @property {string} seeMoreLabel  - Label for see more options label for filter items
 * @property {string} viewLessLabel - Label for view less options
 * @property {string} seeMoreListLength - set the threshold value for list items
 * @property {string} seeMoreAriaLabel - Arialabel for see more button
 * @property {string} viewLessAriaLabel - Arialabel for view less button
 * @property { object } seeMore - object containing filter gropu see more state
 * @property {object} searchBar - enable search in filters
 * @property {string} noResultsLabel - results label
 * @property {array} appliedFilters - array containing applied filters.
 */

export const propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape( {
      title: PropTypes.string,
      count: PropTypes.number,
      selected: PropTypes.bool,
      disabled: PropTypes.bool,
      selectAction: PropTypes.object,
      type: PropTypes.string,
      removeAction: PropTypes.object,
      removeFilterAccessibility: PropTypes.string,
      id: PropTypes.string,
      minLabel: PropTypes.string,
      maxLabel: PropTypes.string,
      rangeCurrency: PropTypes.string,
      labelPositionLeft: PropTypes.bool
    } )
  ),
  title: PropTypes.string,
  seeMoreAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  type: PropTypes.string,
  id: PropTypes.string,
  seeMoreLabel: PropTypes.string,
  viewLessLabel: PropTypes.string,
  seeMoreListLength: PropTypes.number,
  seeMoreAriaLabel: PropTypes.string,
  viewLessAriaLabel: PropTypes.string,
  searchBar: PropTypes.shape( {
    clearAccessibilityLabel: PropTypes.string,
    clearAction: PropTypes.func,
    placeholderLabel: PropTypes.string,
    searchAction: PropTypes.func
  } ),
  noResultsLabel: PropTypes.string,
  appliedFilters: PropTypes.array
};

FilterGroup.propTypes = propTypes;

export default FilterGroup;
